import { useRef, useState } from "react";
import { Loader2, Play, Square, Download, RotateCcw, Sliders, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const DEFAULTS = {
  speed: 1.0,
  reverb: 0.0,
  delayWet: 0.0,
  delayTime: 0.25,
  delayFeedback: 0.3,
  eqLow: 0,
  eqHigh: 0,
  fadeIn: 0,
  fadeOut: 0,
};

const PRESETS = {
  "Salle de concert": { reverb: 0.6, eqLow: 3, eqHigh: 2, fadeIn: 0.5, fadeOut: 1.5 },
  "Rêve / Slow": { speed: 0.8, reverb: 0.5, delayWet: 0.25, delayTime: 0.4, fadeIn: 1, fadeOut: 2 },
  "8-bit sec": { reverb: 0, eqLow: -4, eqHigh: 5, delayWet: 0 },
  "Écho vaste": { delayWet: 0.4, delayTime: 0.5, delayFeedback: 0.45, reverb: 0.3 },
};

function makeImpulse(ctx, seconds = 2.2, decay = 2.5) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const ch = impulse.getChannelData(c);
    for (let i = 0; i < len; i++) {
      ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return impulse;
}

// Wire source -> EQ -> (dry + delay + reverb) -> master. Returns the master gain node.
function connectEffects(ctx, source, o) {
  const low = ctx.createBiquadFilter();
  low.type = "lowshelf";
  low.frequency.value = 250;
  low.gain.value = o.eqLow;
  const high = ctx.createBiquadFilter();
  high.type = "highshelf";
  high.frequency.value = 3000;
  high.gain.value = o.eqHigh;
  source.connect(low);
  low.connect(high);

  const master = ctx.createGain();

  const dry = ctx.createGain();
  dry.gain.value = 1;
  high.connect(dry);
  dry.connect(master);

  if (o.delayWet > 0) {
    const delay = ctx.createDelay(5.0);
    delay.delayTime.value = o.delayTime;
    const fb = ctx.createGain();
    fb.gain.value = Math.min(o.delayFeedback, 0.9);
    const wet = ctx.createGain();
    wet.gain.value = o.delayWet;
    high.connect(delay);
    delay.connect(fb);
    fb.connect(delay);
    delay.connect(wet);
    wet.connect(master);
  }

  if (o.reverb > 0) {
    const conv = ctx.createConvolver();
    conv.buffer = makeImpulse(ctx);
    const wet = ctx.createGain();
    wet.gain.value = o.reverb;
    high.connect(conv);
    conv.connect(wet);
    wet.connect(master);
  }

  return master;
}

function applyFades(gainNode, ctx, t0, effectiveDuration, fadeIn, fadeOut) {
  const g = gainNode.gain;
  g.cancelScheduledValues(t0);
  g.setValueAtTime(fadeIn > 0 ? 0.0001 : 1, t0);
  if (fadeIn > 0) g.linearRampToValueAtTime(1, t0 + fadeIn);
  if (fadeOut > 0 && effectiveDuration > fadeOut) {
    g.setValueAtTime(1, t0 + effectiveDuration - fadeOut);
    g.linearRampToValueAtTime(0.0001, t0 + effectiveDuration);
  }
}

function encodeWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const len = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(len);
  const view = new DataView(ab);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  let off = 0;
  writeStr(off, "RIFF"); off += 4;
  view.setUint32(off, len - 8, true); off += 4;
  writeStr(off, "WAVE"); off += 4;
  writeStr(off, "fmt "); off += 4;
  view.setUint32(off, 16, true); off += 4;
  view.setUint16(off, 1, true); off += 2;
  view.setUint16(off, numCh, true); off += 2;
  view.setUint32(off, buffer.sampleRate, true); off += 4;
  view.setUint32(off, buffer.sampleRate * numCh * 2, true); off += 4;
  view.setUint16(off, numCh * 2, true); off += 2;
  view.setUint16(off, 16, true); off += 2;
  writeStr(off, "data"); off += 4;
  view.setUint32(off, buffer.length * numCh * 2, true); off += 4;
  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      let s = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      off += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

export default function AudioEffectsPanel({ src, downloadName = "processed.wav", onSave, defaultName = "" }) {
  const [o, setO] = useState(DEFAULTS);
  const [playing, setPlaying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState(defaultName);
  const ctxRef = useRef(null);
  const audioRef = useRef(null);
  const srcNodeRef = useRef(null);
  const bufRef = useRef(null); // cached decoded ArrayBuffer

  const set = (k) => (v) => setO((p) => ({ ...p, [k]: Array.isArray(v) ? v[0] : v }));
  const reset = () => setO(DEFAULTS);
  const applyPreset = (name) => setO({ ...DEFAULTS, ...PRESETS[name] });

  const stop = () => {
    try { audioRef.current?.pause(); } catch { /* noop */ }
    if (ctxRef.current) { try { ctxRef.current.close(); } catch { /* noop */ } ctxRef.current = null; }
    srcNodeRef.current = null;
    setPlaying(false);
  };

  const preview = async () => {
    if (playing) { stop(); return; }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const el = new Audio();
      el.crossOrigin = "use-credentials";
      el.src = src;
      audioRef.current = el;
      await el.play().catch(() => {});
      el.pause();
      el.currentTime = 0;
      el.playbackRate = o.speed;
      const node = ctx.createMediaElementSource(el);
      srcNodeRef.current = node;
      const master = connectEffects(ctx, node, o);
      master.connect(ctx.destination);
      const dur = (el.duration && isFinite(el.duration) ? el.duration : 30) / o.speed;
      applyFades(master, ctx, ctx.currentTime, dur, o.fadeIn, o.fadeOut);
      el.onended = () => stop();
      await ctx.resume();
      await el.play();
      setPlaying(true);
    } catch {
      toast.error("Aperçu impossible dans ce navigateur.");
      stop();
    }
  };

  // Render the effect chain offline and return a WAV Blob.
  const renderWav = async () => {
    if (!bufRef.current) {
      const res = await fetch(src, { credentials: "include" });
      bufRef.current = await res.arrayBuffer();
    }
    const decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
    const decoded = await decodeCtx.decodeAudioData(bufRef.current.slice(0));
    decodeCtx.close();

    const outLen = Math.ceil(decoded.length / o.speed) + Math.ceil(decoded.sampleRate * 3);
    const offline = new OfflineAudioContext(decoded.numberOfChannels, outLen, decoded.sampleRate);
    const bs = offline.createBufferSource();
    bs.buffer = decoded;
    bs.playbackRate.value = o.speed;
    const master = connectEffects(offline, bs, o);
    master.connect(offline.destination);
    const effDur = decoded.duration / o.speed;
    applyFades(master, offline, 0, effDur, o.fadeIn, o.fadeOut);
    bs.start(0);
    const rendered = await offline.startRendering();
    return encodeWav(rendered);
  };

  const exportWav = async () => {
    setExporting(true);
    try {
      const blob = await renderWav();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("WAV traité exporté ✨");
    } catch {
      toast.error("Export impossible. Réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const saveAsTrack = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      const blob = await renderWav();
      await onSave(blob, o, saveName.trim());
    } catch {
      toast.error("Rendu impossible. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="audio-effects-panel">
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(PRESETS).map((name) => (
          <button key={name} onClick={() => applyPreset(name)} data-testid={`fx-preset-${name}`}
            className="text-xs px-2.5 py-1 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/15 transition-colors">
            {name}
          </button>
        ))}
        <button onClick={reset} data-testid="fx-reset" className="ml-auto text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <RotateCcw size={12} /> Réinitialiser
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3">
        <Fx label="Vitesse / Pitch" val={o.speed} min={0.5} max={1.5} step={0.05} onChange={set("speed")} fmt={(v) => `${v.toFixed(2)}×`} testId="fx-speed" />
        <Fx label="Reverb" val={o.reverb} min={0} max={1} step={0.05} onChange={set("reverb")} fmt={(v) => `${Math.round(v * 100)}%`} testId="fx-reverb" />
        <Fx label="Écho (mix)" val={o.delayWet} min={0} max={1} step={0.05} onChange={set("delayWet")} fmt={(v) => `${Math.round(v * 100)}%`} testId="fx-delay-wet" />
        <Fx label="Écho (temps)" val={o.delayTime} min={0.05} max={0.8} step={0.05} onChange={set("delayTime")} fmt={(v) => `${Math.round(v * 1000)}ms`} testId="fx-delay-time" />
        <Fx label="EQ Grave" val={o.eqLow} min={-15} max={15} step={1} onChange={set("eqLow")} fmt={(v) => `${v > 0 ? "+" : ""}${v}dB`} testId="fx-eq-low" />
        <Fx label="EQ Aigu" val={o.eqHigh} min={-15} max={15} step={1} onChange={set("eqHigh")} fmt={(v) => `${v > 0 ? "+" : ""}${v}dB`} testId="fx-eq-high" />
        <Fx label="Fade in" val={o.fadeIn} min={0} max={5} step={0.25} onChange={set("fadeIn")} fmt={(v) => `${v.toFixed(2)}s`} testId="fx-fade-in" />
        <Fx label="Fade out" val={o.fadeOut} min={0} max={5} step={0.25} onChange={set("fadeOut")} fmt={(v) => `${v.toFixed(2)}s`} testId="fx-fade-out" />
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={preview} variant="outline" className={playing ? "border-destructive/50 text-destructive gap-2" : "border-primary/40 text-primary hover:bg-primary/10 gap-2"} data-testid="fx-preview">
          {playing ? <><Square size={15} /> Arrêter</> : <><Play size={15} /> Aperçu</>}
        </Button>
        <Button onClick={exportWav} disabled={exporting} className="bg-secondary text-white hover:bg-secondary/90 gap-2" data-testid="fx-export">
          {exporting ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Exporter WAV traité
        </Button>
      </div>
      {onSave && (
        <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-3 space-y-2" data-testid="fx-save-panel">
          <div className="text-[10px] font-mono text-primary flex items-center gap-1.5"><Save size={12} /> ENREGISTRER DANS LA LIBRAIRIE</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Nom de la nouvelle piste" className="bg-background border-border h-9 text-sm" data-testid="fx-save-name" />
            <Button onClick={saveAsTrack} disabled={saving} className="h-9 bg-primary text-primary-foreground hover:bg-primary/90 gap-2 shrink-0" data-testid="fx-save-track">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Enregistrer comme nouvelle piste
            </Button>
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Sliders size={12} /> Traitement 100 % local — aucun crédit consommé.</p>
    </div>
  );
}

const Fx = ({ label, val, min, max, step, onChange, fmt, testId }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono text-xs text-primary">{fmt(val)}</span>
    </div>
    <Slider value={[val]} min={min} max={max} step={step} onValueChange={onChange} data-testid={testId} />
  </div>
);
